import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  adminJobsFailedDestroy,
  adminJobsFailedList,
  adminJobsFailedRetrieve,
  adminJobsFailedRetryCreate,
  adminJobsHealthRetrieve,
  type FailedJob,
} from "@motolynk/api-client";
import { formatDateTime, usePermissions, useUiLabels } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Alert,
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  KpiTile,
  LoadingState,
  PageHeader,
  Select,
  toast,
  useConfirm,
} from "@motolynk/ui";

export const jobKeys = {
  all: ["admin", "jobs"] as const,
  health: () => ["admin", "jobs", "health"] as const,
  failed: (params: object) => ["admin", "jobs", "failed", params] as const,
  detail: (uid: string) => ["admin", "jobs", "failed", "detail", uid] as const,
};

const PAGE_SIZE = 25;

/** Background jobs: is the worker alive, what is waiting, and the jobs that failed (retry or delete them). */
export default function JobsPage() {
  const { t, i18n } = useTranslation("ops");
  const ui = useUiLabels();
  const [show, setShow] = useState<"open" | "retried" | "all">("open");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const language = i18n.language;
  const health = useQuery({
    queryKey: jobKeys.health(),
    queryFn: async () => (await adminJobsHealthRetrieve()).data,
    refetchInterval: 30_000,
  });
  const query = {
    page,
    page_size: PAGE_SIZE,
    ...(show === "all" ? {} : { retried: show === "retried" }),
  };
  const failed = useQuery({
    queryKey: jobKeys.failed(query),
    queryFn: async () => (await adminJobsFailedList(query)).data,
    placeholderData: keepPreviousData,
  });
  const columns = useMemo<ColumnDef<FailedJob>[]>(
    () => [
      {
        id: "job",
        header: t("columns.job"),
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="font-mono text-sm">{row.original.job_name}</p>
            <p className="truncate text-xs text-muted-foreground">{row.original.exception}</p>
          </div>
        ),
      },
      { id: "attempts", header: t("columns.attempts"), cell: ({ row }) => row.original.attempts },
      {
        id: "failed",
        header: t("columns.failedAt"),
        cell: ({ row }) => formatDateTime(row.original.failed_at, language),
      },
      {
        id: "state",
        header: t("columns.state"),
        cell: ({ row }) =>
          row.original.retried_at ? (
            <Badge tone="info">{t("retried")}</Badge>
          ) : (
            <Badge tone="danger">{t("open")}</Badge>
          ),
      },
    ],
    [t, language],
  );
  const h = health.data;

  return (
    <>
      <PageHeader title={t("title")} description={t("description")} />
      {h && !h.worker.alive && (
        <Alert tone="danger" title={t("workerDown")} className="mb-4">
          {t("workerDownHint")}
        </Alert>
      )}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          icon="refresh"
          label={t("worker")}
          value={h ? (h.worker.alive ? t("running") : t("stopped")) : "—"}
          hint={
            h?.worker.last_seen
              ? t("lastSeen", { date: formatDateTime(h.worker.last_seen, language) })
              : h
                ? t("neverSeen")
                : undefined
          }
        />
        <KpiTile icon="inbox" label={t("pending")} value={h ? h.pending : "—"} hint={h?.driver} />
        <KpiTile icon="loader" label={t("scheduled")} value={h ? h.scheduled : "—"} />
        <KpiTile icon="alert" label={t("failed")} value={h ? h.failed : "—"} />
      </div>
      <div className="mb-4 flex justify-end">
        <Select
          className="sm:w-48"
          aria-label={t("show")}
          value={show}
          onValueChange={(value) => {
            setShow(value as typeof show);
            setPage(1);
          }}
          options={(["open", "retried", "all"] as const).map((value) => ({
            value,
            label: t(`filter.${value}`),
          }))}
        />
      </div>
      {failed.isError ? (
        <ErrorState
          title={t("loadFailed")}
          retryLabel={ui.retry}
          onRetry={() => void failed.refetch()}
        />
      ) : (
        <DataTable
          columns={columns}
          data={failed.data?.results ?? []}
          total={failed.data?.count ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={failed.isPending}
          getRowId={(row) => row.uid}
          onRowClick={(row) => setOpen(row.uid)}
          labels={{ ...ui.table, emptyTitle: t("empty") }}
        />
      )}
      {open && <JobDialog uid={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function JobDialog({ uid, onClose }: { uid: string; onClose: () => void }) {
  const { t, i18n } = useTranslation("ops");
  const ui = useUiLabels();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const job = useQuery({
    queryKey: jobKeys.detail(uid),
    queryFn: async () => (await adminJobsFailedRetrieve(uid)).data,
  });
  const done = async (message: string) => {
    toast.success(message);
    onClose();
    await queryClient.invalidateQueries({ queryKey: jobKeys.all });
  };
  const retry = useMutation({
    mutationFn: async () => adminJobsFailedRetryCreate(uid),
    onSuccess: () => done(t("retryQueued")),
    onError: (error) => toast.error(error instanceof Error ? error.message : String(error)),
  });
  const confirm = useConfirm();
  const remove = useMutation({
    mutationFn: async () => adminJobsFailedDestroy(uid),
    onSuccess: () => done(t("deleted")),
    onError: (error) => toast.error(error instanceof Error ? error.message : String(error)),
  });
  return (
    <Dialog open onOpenChange={(state) => !state && onClose()}>
      <DialogContent closeLabel={ui.close} className="sm:max-w-3xl">
        {job.isPending ? (
          <LoadingState />
        ) : job.isError ? (
          <ErrorState
            title={t("loadFailed")}
            retryLabel={ui.retry}
            onRetry={() => void job.refetch()}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-mono">{job.data.job_name}</DialogTitle>
              <DialogDescription>
                {formatDateTime(job.data.failed_at, i18n.language)} ·{" "}
                {t("attempts", { count: job.data.attempts })}
                {job.data.request_id ? ` · ${job.data.request_id}` : ""}
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm font-medium">{job.data.exception}</p>
            <pre className="max-h-80 overflow-auto rounded-lg bg-surface-muted p-3 text-xs">
              {job.data.traceback}
            </pre>
            <DialogFooter>
              {can("core.delete_failedjob") && (
                <Button
                  variant="ghost"
                  loading={remove.isPending}
                  onClick={() =>
                    confirm.ask({
                      title: t("confirmDeleteTitle"),
                      description: t("confirmDeleteHint"),
                      confirmLabel: t("delete"),
                      onConfirm: () => remove.mutateAsync(),
                    })
                  }
                >
                  {t("delete")}
                </Button>
              )}
              {can("core.change_failedjob") && !job.data.retried_at && (
                <Button loading={retry.isPending} onClick={() => retry.mutate()}>
                  {t("retry")}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
        {confirm.dialog}
      </DialogContent>
    </Dialog>
  );
}
